/* istanbul ignore file */
import { FormSection, FormRow, BigFormRow } from 'framework/ui/layout';
import { DateInput, TextArea, TextInput, Checkbox, ClearAfterUnmountWrapper, useInputHelper } from 'framework/form';
import { BudgetCategoryDropdown } from 'domain/shared/budget';
import { isBudgetCategoryRequired } from 'domain/shared/budget';
import { VendorDropdown } from 'domain/shared/vendor';
import useLedgerService from 'domain/finance/ledger/useLedgerService';
import { OfficeCodeDropdown } from 'domain/shared/employee';
import { InvoiceTotalInput } from 'domain/shared/core/invoice';
import CurrencyDropdownWithoutEur from 'domain/finance/shared/input/CurrencyDropdownWithoutEur';
import { AccountInput } from 'domain/shared/ledger';
import PurchaseOrderDropdown from 'domain/finance/purchaseOrder/shared/input/PurchaseOrderDropdown';
import InvoiceClassificationDropdown from 'domain/finance/invoice/shared/input/InvoiceClassificationDropdown';
import { NoteAttachmentUploaderInput } from 'domain/shared/artifact/input';
import { ExpenseProjectDropdown, isProjectSuggestionAccount } from 'domain/shared/project';

const isProjectSuggestionVisible = (data: any): boolean =>
  !data.purchaseOrderId && isProjectSuggestionAccount(data.account);

const InvoiceForm = () => {
  const { searchInvoiceAccount } = useLedgerService();
  const { getValue: getOffice } = useInputHelper<string>({ source: 'office' });
  const office = getOffice();
  const { getValue: getAccount } = useInputHelper<string>({ source: 'account' });
  const account = getAccount();
  const { getValue: getInvoiceDate } = useInputHelper<string>({ source: 'invoiceDate' });
  const invoiceDate = getInvoiceDate();

  return (
    <>
      <FormSection title="Main information">
        <FormRow>
          <Checkbox
            tooltip="Select this option to upload payment document and indicate the Invoice number later, when vendor provides it."
            source="isInvoiceDocumentWillArriveLater"
            label="Will the invoice arrive later?"
          />
          <Checkbox
            tooltip="Select this option if goods/services well be provided in a month other than the invoice date."
            source="isFutureExpense"
            label="Is future expense?"
          />
        </FormRow>
        <FormRow>
          <TextInput source="invoiceNumber" label="Invoice number" />
          <DateInput label="Date" source="invoiceDate" />
          <OfficeCodeDropdown source="office" />
        </FormRow>
        <FormRow>
          <VendorDropdown source="vendor" label="Vendor" placeholder="Select vendor" />
          <TextInput source="name" label="Vendor name" />
          <InvoiceTotalInput source="subTotal.amount" currencySource="subTotal.currency" label="Total amount" />
          <CurrencyDropdownWithoutEur source="subTotal.currency" label="Total currency" />
          <ClearAfterUnmountWrapper isVisible={data => !data.purchaseOrderId}>
            <AccountInput
              source="account"
              label="Account"
              cacheKey="invoice-accounts"
              loadDataMethod={searchInvoiceAccount}
            />
          </ClearAfterUnmountWrapper>
          <ClearAfterUnmountWrapper
            isVisible={(data: any) => data.invoiceDate && data.account && !data.purchaseOrderId}
          >
            <BudgetCategoryDropdown
              disabled={!isBudgetCategoryRequired(account)}
              source="budgetCategory"
              label="Budget category"
              dateValue={invoiceDate}
              accountIdValue={account}
            />
          </ClearAfterUnmountWrapper>
          <ClearAfterUnmountWrapper isVisible={isProjectSuggestionVisible}>
            <ExpenseProjectDropdown source="project.projectId" label="Project" />
          </ClearAfterUnmountWrapper>
          <PurchaseOrderDropdown
            disabled={!office}
            officeData={office}
            source="purchaseOrderId"
            label="Purchase order"
          />
          <InvoiceClassificationDropdown source="isPurchaseClassifiedAsAsset" label="Purchase classification" />
        </FormRow>
        <BigFormRow>
          <TextArea source="description" />
        </BigFormRow>
        <BigFormRow>
          <NoteAttachmentUploaderInput label="Payment document" source="invoiceDocumentId" />
        </BigFormRow>
      </FormSection>

      <FormSection title="Vendor requisites">
        <FormRow>
          <TextInput source="requisites.bankName" label="Bank name" />
          <TextInput source="requisites.bankAccount" label="Bank account" />
          <TextInput source="requisites.recipient" label="Recipient" />
        </FormRow>
      </FormSection>
    </>
  );
};

export default InvoiceForm;
